import {useSegmentsByGroupId} from '@/hooks/useSegmentsByGroupId'
import {useSegmentsList} from '@/hooks/useSegmentsList'
import {getSegmentsByGroupId} from '@/services/segments'
import {useQueryClient} from '@tanstack/react-query'
import {useCallback, useMemo, useState} from 'react'

/**
 * @typedef {import('@/types/promotionTypes').SegmentDetails} SegmentDetails
 */

/**
 * Custom hook that manages segment folder selection logic.
 * Handles folder browsing, segment fetching, and selection state for a two-panel folder/segment interface.
 *
 * @param {Object} params
 * @param {SegmentDetails[]} params.selectedSegments - Array of currently selected segment objects
 * @param {Function} params.setSelectedSegments - Setter function to update selected segments array
 * @returns {Object} Folder and segment list data, loading states, and event handlers
 */
export const useSegmentFolders = ({selectedSegments, setSelectedSegments}) => {
  const [selectedSegmentFolderId, setSelectedSegmentFolderId] = useState('')
  const queryClient = useQueryClient()

  const {segmentsGroups: segmentsFolders, isLoading: isLoadingFolders} = useSegmentsList()
  const {data: segmentsByFolderData, isLoading: isLoadingSegmentsByFolder} =
    useSegmentsByGroupId(selectedSegmentFolderId)

  const segmentsFoldersItems = useMemo(() => {
    return segmentsFolders.map(segment => {
      const selected = selectedSegmentFolderId === segment.segmentGroupId
      const selectedCount = selectedSegments.filter(
        selectedSegment => selectedSegment.segmentFolderId === segment.segmentGroupId
      ).length
      const cachedSegments = queryClient.getQueryData(['SEGMENTS_BY_GROUP', segment.segmentGroupId])
      const totalCount = cachedSegments?.data?.length ?? segment.segmentCount
      return {
        label: segment.segmentGroupName,
        id: segment.segmentGroupId,
        selected,
        checked: selectedCount > 0,
        subLabel: selectedCount > 0 ? `${selectedCount}/${totalCount} Selected` : undefined,
      }
    })
  }, [segmentsFolders, selectedSegmentFolderId, selectedSegments, queryClient])

  const segmentsItems = useMemo(() => {
    return segmentsByFolderData?.data?.map(segment => ({
      label: segment.segmentName ?? segment,
      id: segment.segmentId,
      segmentFolderId: selectedSegmentFolderId,
      checked: Boolean(
        selectedSegments.find(
          selectedSegment =>
            selectedSegment.segmentId === segment.segmentId &&
            selectedSegment.segmentFolderId === selectedSegmentFolderId
        )
      ),
    }))
  }, [segmentsByFolderData?.data, selectedSegmentFolderId, selectedSegments])

  const onFolderClick = useCallback(item => {
    setSelectedSegmentFolderId(item.id)
  }, [])

  const onFolderCheckboxChange = useCallback(
    async (item, checked) => {
      if (checked) {
        setSelectedSegmentFolderId(item.id)

        const result = await queryClient.fetchQuery({
          queryKey: ['SEGMENTS_BY_GROUP', item.id],
          queryFn: () => getSegmentsByGroupId(item.id, 0, 500),
        })

        const segments = result?.data || []
        const segmentsToAdd = segments.map(segment => ({
          segmentId: segment.segmentId,
          segmentName: segment.segmentName,
          segmentFolderId: item.id,
        }))

        setSelectedSegments(prev => {
          const newSegmentIds = new Set(segmentsToAdd.map(s => s.segmentId))
          const filtered = prev.filter(s => s.segmentFolderId !== item.id && !newSegmentIds.has(s.segmentId))
          return [...filtered, ...segmentsToAdd]
        })
      } else {
        setSelectedSegments(prev => prev.filter(s => s.segmentFolderId !== item.id))
      }
    },
    [queryClient, setSelectedSegments]
  )

  const onSegmentCheckboxChange = useCallback(
    ({id, label, segmentFolderId}, checked) => {
      setSelectedSegments(prevSelected => {
        if (checked) {
          const exists = prevSelected.some(segment => segment.segmentId === id)
          if (exists) {
            return prevSelected.map(segment => (segment.segmentId === id ? {...segment, segmentFolderId} : segment))
          }
          return [...prevSelected, {segmentId: id, segmentName: label, segmentFolderId}]
        } else {
          return prevSelected.filter(segment => segment.segmentId !== id)
        }
      })
    },
    [setSelectedSegments]
  )

  return {
    selectedSegmentFolderId,
    segmentsFoldersItems,
    segmentsItems,
    isLoadingFolders,
    isLoadingSegmentsByFolder,
    onFolderClick,
    onFolderCheckboxChange,
    onSegmentCheckboxChange,
  }
}
